import { Delete, Done, OpenInFull } from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReplayIcon from '@mui/icons-material/Replay';
import { Box, Checkbox, Skeleton } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from 'react-intl';
import { useNavigate } from "react-router-dom";
import CustomSpeedDial from "../components/CustomSpeedDial";
import { useApi } from '../hooks/useApi';
import { API_URL } from "../utils/lib";
import CustomInputElement from './helper/CustomInputElement';

const DataCard = memo(({ userInfo, data, abstractType, selectable, onSelect, onDelete, onDownload,
  trash = false, initialChecked = false, handleChangeCheckbox, multiSelectEnabled = false,
  clearCounter }) => {
  const navigate = useNavigate();
  const [actions, setActions] = useState([]);
  const [imageLoaded, setImageLoaded] = useState(false);

  // copy of the provided data through props
  const [localData, setLocalData] = useState(data);

  const intl = useIntl();
  const api = useApi();

  // state for changing-name-input
  const [inputOpen, setInputOpen] = useState(false);

  // the state for the checkbox
  const [checked, setChecked] = useState(initialChecked ? true : false);

  /**
   * Only used for resetting the checked-state.
   */
  useEffect(() => {
    setChecked(initialChecked);
  }, [clearCounter]);

  /**
   * Opens the naming-input.
   */
  const handleInputOpen = () => {
    setInputOpen(true);
  }

  /**
   * Closes the naming-input.
   */
  const handleInputClose = () => {
    setInputOpen(false);
  }

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const openData = useCallback(() => {
    if (abstractType === 'pdf') {
      return openPDF();
    } else if (abstractType === 'image') {
      return openImage();
    }
  }, [abstractType]);

  /**
   * Called to open an image in a new window.
   */
  const openImage = () => {
    navigate(`/image`, { state: `/data/${localData.id}/file` });
  }

  /**
   * Called to open a pdf in a new tab.
   */
  const openPDF = () => {
    api
      .get(`/data/${localData.id}/file`, { responseType: 'blob' })
      .then(response => {
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const pdfUrl = window.URL.createObjectURL(blob);
        window.open(pdfUrl, '_blank');
      })
      .catch(error => {
        console.log(error);
      })
  }

  /**
   * Sets the possible actions on this data-card.
   */
  useEffect(() => {
    setActions(() => {
      const actions = [];
      if (trash) {
        if (abstractType === 'pdf' || abstractType === 'image') {
          // enable opening
          actions.push(
            { icon: <ReplayIcon />, name: intl.formatMessage({ id: "Data.restore" }), func: onSelect(localData) },
            { icon: <OpenInFull />, name: intl.formatMessage({ id: "Data.open" }), func: openData },
            { icon: <Delete />, name: intl.formatMessage({ id: "Data.delete" }), func: onDelete(localData.id) }
          );
        } else {
          // no opening
          actions.push(
            { icon: <ReplayIcon />, name: intl.formatMessage({ id: "Data.restore" }), func: onSelect(localData) },
            { icon: <Delete />, name: intl.formatMessage({ id: "Data.delete" }), func: onDelete(localData.id) }
          );
        }
      } else if (selectable) {
        if (abstractType === 'pdf' || abstractType === 'image') {
          // enable opening
          actions.push(
            { icon: <Done />, name: intl.formatMessage({ id: "Data.select" }), func: onSelect(localData) },
            { icon: <OpenInFull />, name: intl.formatMessage({ id: "Data.open" }), func: openData }
          );
        } else {
          // no opening
          actions.push(
            { icon: <Done />, name: intl.formatMessage({ id: "Data.select" }), func: onSelect(localData) }
          );
        }
      } else {
        if (abstractType === 'pdf' || abstractType === 'image') {
          // enable opening
          actions.push(
            { icon: <OpenInFull />, name: intl.formatMessage({ id: "Data.open" }), func: openData },
            { icon: <Delete />, name: intl.formatMessage({ id: "Data.delete" }), func: onDelete(localData.id) }
          );
        } else {
          // no opening
          actions.push(
            { icon: <Delete />, name: intl.formatMessage({ id: "Data.delete" }), func: onDelete(localData.id) }
          );
        }
      }
      if (userInfo?.isAdmin) {
        actions.push({ icon: <DownloadIcon />, name: intl.formatMessage({ id: "Data.download" }), func: onDownload(localData) });
      }
      return actions;
    });
  }, [selectable, localData, abstractType, trash])

  /**
   * Should be called, when the enclosing box is clicked on.
   * If the DataCard is in selectable-mode, selects the current data.
   * If it is not in selectable-mode, do nothing.
   */
  const handleBoxClick = () => {
    // if we are in selectable-mode, select the current data
    if (selectable) {
      onSelect(localData)();
    }
  };

  const d = useMemo(() => {
    if (abstractType === 'pdf') {
      return <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}><PictureAsPdfIcon sx={{ fontSize: '40px' }} /></div>
    } else if (abstractType === 'image') {
      return (
        <div style={{ width: '90%', height: '60%' }}>
          {
            !imageLoaded && (
              <Skeleton variant="rectangular" width="100%" height="100%" />
            )
          }
          <img style={{ width: '100%', objectFit: 'contain', maxHeight: '100%', display: imageLoaded ? 'block' : 'none' }} onLoad={handleImageLoad} src={API_URL + `/data/${localData.id}/file?quality=thumb`} />
        </div>
      )
    } else {
      // return single div for positioning name and actions at bottom
      return <div></div>
    }
  }, [abstractType, imageLoaded]);

  /**
   * Checks if the name is not blank and then sends the change to the server.
   * @param {*} name 
   * @returns 
   */
  const changeDataName = (name) => {
    name = name.trim();
    if (!name || name.match(/^\s*$/)) {
      // do not change the name, because the name is blank
      handleInputClose();
      return;
    }
    api
      .put(`/data/${localData.id}?name=${name}`)
      .then(response => {
        setLocalData(old => ({ ...old, fileName: name }));
        handleInputClose();
      })
      .catch(error => {
        console.log(error);
      });
  };

  /**
  * Should be called when the checkbox changes its value.
  * @param {*} event 
  */
  const onChangeCheckbox = (event) => {
    setChecked(event.target.checked);
    handleChangeCheckbox(localData.id, event.target.checked);
  };

  /**
   * Either showing the name, or letting the user rename the data-entity.
   */
  const nameField = useMemo(() => {
    const style = {
      width: "calc(100% - 100px)",
      marginLeft: '16px',
      maxHeight: '100%',
      padding: '2px',
      fontSize: '16px'
    }
    if (inputOpen) {
      return <CustomInputElement initialValue={localData.fileName} handleValueChange={changeDataName} style={style} />
    } else {
      return <p style={{
        ...style,
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        wordBreak: 'break-all',
        border: '1px dashed',
      }}
        onDoubleClick={handleInputOpen}
      >{localData.fileName}</p>
    }
  }, [inputOpen, localData]);

  return (
    <Box onClick={handleBoxClick} sx={{ paddingTop: '2px', width: '100%', height: '300px', backgroundColor: '#bfc9c2', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', cursor: selectable ? 'pointer' : 'auto', position: 'relative' }}>
      {d}
      {multiSelectEnabled && <Checkbox
        style={{ position: 'absolute', top: '0px', right: '0px' }}
        checked={checked}
        onChange={onChangeCheckbox}
      />}
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxHeight: '40%' }}>
        {nameField}
        <CustomSpeedDial actions={actions} />
      </Box>
    </Box>
  )
});

export default DataCard;